<template>
  <div>
    <back-button default-to="/stock-adjustments"/>
    <b-card no-header>
      <div class="d-flex mb-3">
        <div>
          <h5 class="mb-1">{{ detail.transaction_no }}</h5>
          <div>{{ formatDate(detail.tanggal_penyesuaian) }} · {{ jenisLabel(detail.jenis) }}</div>
        </div>
        <div class="ml-auto">
          <b-badge :variant="statusVariant(detail.approval_status)">{{ statusLabel(detail.approval_status) }}</b-badge>
        </div>
      </div>
      <b-alert :show="detail.approval_status === 'draft' && !!detail.returned_reason" variant="warning">
        Dikembalikan untuk diperbaiki: {{ detail.returned_reason }}
      </b-alert>
      <b-alert :show="detail.approval_status === 'rejected' && !!detail.rejected_reason" variant="danger">
        Ditolak oleh owner: {{ detail.rejected_reason }}
      </b-alert>
      <p v-if="detail.submitted_at" class="text-muted small">Diajukan oleh {{ actorName(detail.submitter) }} pada {{ formatDateTime(detail.submitted_at) }}.</p>
      <p v-if="detail.edited_at" class="text-muted small">Terakhir diubah oleh {{ actorName(detail.editor) }} pada {{ formatDateTime(detail.edited_at) }}.</p>
      <p v-if="detail.alasan"><strong>Alasan:</strong> {{ detail.alasan }}</p>
      <div class="table-responsive">
        <table class="table table-striped">
          <thead><tr><th>Nama</th><th>Jenis</th><th>Berat</th><th>Kadar</th><th>Jumlah</th><th>Harga Modal</th></tr></thead>
          <tbody>
            <tr v-for="item in detail.detail_barang" :key="item.id">
              <td>{{ item.nama }}<small v-if="item.stock_id" class="d-block text-muted">Stok #{{ item.stock_id }}</small></td>
              <td>{{ typeLabel(item.jenis_barang) }}</td>
              <td>{{ item.berat }} gram</td>
              <td>{{ item.kadar }}</td>
              <td>{{ item.qty }} buah</td>
              <td>Rp{{ $formatPrice(item.harga_modal) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="isAdmin && detail.approval_status === 'pending'" class="mt-3">
        <b-button variant="success" :disabled="isLoading" @click="approve">Setujui dan Perbarui Saldo Stok</b-button>
        <b-button variant="warning" class="ml-2" :disabled="isLoading" @click="showReturn">Kembalikan ke Draft</b-button>
        <b-button variant="danger" class="ml-2" @click="showReject">Tolak</b-button>
      </div>
      <div v-if="canEditDraft" class="mt-3">
        <b-button variant="primary" :to="'/stock-adjustments/' + detail.id + '/edit'">Ubah Draft</b-button>
        <b-button variant="success" class="ml-2" :disabled="isLoading" @click="submit">Ajukan Persetujuan</b-button>
        <b-button variant="danger" class="ml-2" :disabled="isLoading" @click="removeDraft">Hapus Draft</b-button>
      </div>
      <b-button to="/stock-adjustments" variant="secondary" class="float-right">Kembali</b-button>
    </b-card>
    <b-modal ref="rejectModal" title="Tolak Penyesuaian Stok" ok-title="Tolak" cancel-title="Batal" @ok="reject">
      <form-group-input type="textarea" label="Alasan Penolakan" :need-label="true" v-model="rejectReason" id="reject_reason" />
    </b-modal>
    <b-modal ref="returnModal" title="Kembalikan ke Draft" ok-title="Kembalikan" cancel-title="Batal" @ok="returnForRevision">
      <form-group-input type="textarea" label="Alasan Pengembalian" :need-label="true" v-model="returnReason" id="returned_reason" />
    </b-modal>
  </div>
</template>

<script>
import StockAdjustment from '@/models/StockAdjustment.js'
import formGroupInput from '@/components/FormGroupInput.vue'
import { Roles } from '@/_helpers'

const stockAdjustmentModel = new StockAdjustment()

export default {
  components: { formGroupInput },
  data () {
    return {
      detail: { detail_barang: [] },
      isLoading: false,
      rejectReason: '',
      returnReason: ''
    }
  },
  computed: {
    isAdmin () {
      return this.$store.getters['account/accountData'].role === Roles.ADMIN
    },
    isCreator () {
      return Number(this.detail.created_by) === Number(this.$store.getters['account/accountData'].id)
    },
    canEditDraft () {
      return this.detail.approval_status === 'draft' && this.isCreator
    },
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.detail = await stockAdjustmentModel.find(this.$route.params.id)
    },
    statusLabel (status) {
      return { draft: 'Draft', pending: 'On Review', approved: 'Disetujui', rejected: 'Ditolak' }[status] || status
    },
    statusVariant (status) {
      return { draft: 'secondary', pending: 'warning', approved: 'success', rejected: 'danger' }[status] || 'secondary'
    },
    typeLabel (value) {
      return { emas: 'Emas', berlian: 'Berlian', lainnya: 'Lainnya', perhiasan: 'Lainnya' }[value] || value
    },
    jenisLabel (value) {
      return { tambah: 'Tambah Stok', kurang: 'Kurangi Stok' }[value] || value
    },
    formatDate (value) {
      return value ? this.$moment(value).format('DD/MM/YYYY') : '-'
    },
    formatDateTime (value) {
      return value ? this.$moment(value).format('DD/MM/YYYY HH:mm') : '-'
    },
    actorName (actor) {
      return actor && actor.name ? actor.name : '-'
    },
    approve () {
      this.isLoading = true
      stockAdjustmentModel.approve(this.$route.params.id).then(response => {
        this.detail = response
        this.$store.dispatch('notification/success', 'Penyesuaian stok berhasil disetujui dan saldo stok diperbarui.')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    submit () {
      this.isLoading = true
      stockAdjustmentModel.submit(this.$route.params.id).then(response => {
        this.detail = response
        this.$store.dispatch('notification/success', 'Penyesuaian Stok berhasil diajukan untuk persetujuan.')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    removeDraft () {
      if (!window.confirm('Hapus draft Penyesuaian Stok ini?')) return
      this.isLoading = true
      stockAdjustmentModel.delete(this.$route.params.id).then(() => {
        this.$store.dispatch('notification/success', 'Draft Penyesuaian Stok dihapus.')
        this.$router.push('/stock-adjustments')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    showReject () {
      this.$refs.rejectModal.show()
    },
    showReturn () {
      this.$refs.returnModal.show()
    },
    returnForRevision (event) {
      if (!this.returnReason) {
        event.preventDefault()
        this.$store.dispatch('notification/error', 'Alasan pengembalian wajib diisi.')
        return
      }
      this.isLoading = true
      stockAdjustmentModel.returnForRevision(this.$route.params.id, this.returnReason).then(response => {
        this.detail = response
        this.returnReason = ''
        this.$store.dispatch('notification/success', 'Penyesuaian Stok dikembalikan ke draft untuk diperbaiki.')
      }).catch(error => this.$store.dispatch('notification/error', error)).finally(() => { this.isLoading = false })
    },
    reject (event) {
      if (!this.rejectReason) {
        event.preventDefault()
        this.$store.dispatch('notification/error', 'Alasan penolakan wajib diisi')
        return
      }
      stockAdjustmentModel.reject(this.$route.params.id, this.rejectReason).then(response => {
        this.detail = response
        this.$store.dispatch('notification/success', 'Pengajuan penyesuaian stok ditolak.')
      }).catch(error => this.$store.dispatch('notification/error', error))
    }
  }
}
</script>
